import Service from '@/Network/Services/Service'
import request from '@/Network/Requests/request'
import type {
  ListQualificationTransferResponse,
  QualificationTransferPolicyDetail,
  ListQualificationTransferDoctorsResponse,
  QualificationTransferDoctor,
  ListQualificationTransferPendingResponse,
  ListQualificationTransferDomainResponse,
  ListQualificationTransferLinksResponse,
  QualificationTransferLink,
  ListAvailableDoctorsResponse,
  ListQualificationTransferPolicyLinksResponse,
  ListQualificationTransferPolicyOptionsResponse
} from '@/Network/Types/Responses/QualificationTransferResponses'
import type {
  QualificationTransferSearchParams,
  QualificationTransferDoctorSearchParams,
  UpdateQualificationTransferPolicyRequest,
  ToggleDoctorFieldRequest,
  CreateQualificationTransferPolicyRequest,
  ToggleQualificationTransferPolicyRequest,
  DuplicateQualificationTransferPolicyRequest,
  QualificationTransferPendingSearchParams,
  CreateQualificationTransferLinkRequest,
  UpdateQualificationTransferLinkRequest,
  AddDoctorsToQualificationTransferPolicyRequest,
  AvailableDoctorsSearchParams,
  UpdateQualificationTransferPolicyLinksRequest,
  QualificationTransferPolicyOptionsSearchParams
} from '@/Network/Types/Requests/QualificationTransferRequests'

export default class QualificationTransferService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/qualification-transfer-policy'
  }

  async list(
    params?: QualificationTransferSearchParams
  ): Promise<ListQualificationTransferResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferResponse>(
        this.getCompletePath(),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as políticas de repasse.')
    }
  }

  async getById(id: number): Promise<QualificationTransferPolicyDetail> {
    try {
      const { data } = await request.get<QualificationTransferPolicyDetail>(
        this.getCompletePath(`/${id}`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os detalhes da política de repasse.')
    }
  }

  async create(
    params: CreateQualificationTransferPolicyRequest
  ): Promise<QualificationTransferPolicyDetail> {
    try {
      const { data } = await request.post<QualificationTransferPolicyDetail>(
        this.getCompletePath(),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar a política de repasse.')
    }
  }

  async updatePolicy(
    id: number,
    params: UpdateQualificationTransferPolicyRequest
  ): Promise<QualificationTransferPolicyDetail> {
    try {
      const { data } = await request.patch(this.getCompletePath(`/${id}`), params)
      return data as QualificationTransferPolicyDetail
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar as alterações da política de repasse.')
    }
  }

  async toggleActive(
    id: number,
    params: ToggleQualificationTransferPolicyRequest
  ): Promise<QualificationTransferPolicyDetail> {
    try {
      const { data } = await request.patch(this.getCompletePath(`/${id}/toggle`), params)
      return data as QualificationTransferPolicyDetail
    } catch (e) {
      throw this.handleError(e, 'Não foi possível alterar a situação da política de repasse.')
    }
  }

  async duplicate(
    id: number,
    params?: DuplicateQualificationTransferPolicyRequest
  ): Promise<QualificationTransferPolicyDetail> {
    try {
      const { data } = await request.post<QualificationTransferPolicyDetail>(
        this.getCompletePath(`/${id}/duplicate`),
        params ?? {}
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível duplicar a política de repasse.')
    }
  }

  async listDoctors(
    policyId: number,
    params?: QualificationTransferDoctorSearchParams
  ): Promise<ListQualificationTransferDoctorsResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferDoctorsResponse>(
        this.getCompletePath(`/${policyId}/doctors`),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os médicos da política de repasse.')
    }
  }

  async listDoctorsBySchedule(
    scheduleId: number,
    params?: QualificationTransferDoctorSearchParams
  ): Promise<ListQualificationTransferDoctorsResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferDoctorsResponse>(
        `/schedules/${scheduleId}/qualification-transfer-doctors`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos da escala para a política de repasse.'
      )
    }
  }

  async listAvailableDoctors(
    policyId: number,
    params?: AvailableDoctorsSearchParams
  ): Promise<ListAvailableDoctorsResponse> {
    try {
      const { data } = await request.get<ListAvailableDoctorsResponse>(
        this.getCompletePath(`/${policyId}/available-doctors`),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos disponíveis para a política de repasse.'
      )
    }
  }

  async toggleDoctorField(
    policyId: number,
    params: ToggleDoctorFieldRequest
  ): Promise<QualificationTransferDoctor> {
    try {
      const { doctorId, ...body } = params
      const { data } = await request.patch(
        this.getCompletePath(`/${policyId}/doctors/${doctorId}`),
        body
      )
      return data as QualificationTransferDoctor
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar os critérios de repasse do médico.')
    }
  }

  async addDoctors(
    policyId: number,
    params: AddDoctorsToQualificationTransferPolicyRequest
  ): Promise<void> {
    try {
      await request.post(this.getCompletePath(`/${policyId}/doctors`), params)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar os médicos à política de repasse.')
    }
  }

  async removeDoctor(policyId: number, doctorId: number): Promise<void> {
    try {
      await request.delete(this.getCompletePath(`/${policyId}/doctors/${doctorId}`))
    } catch (e) {
      throw this.handleError(e, 'Não foi possível remover o médico da política de repasse.')
    }
  }

  async listPendingEvaluations(
    policyId: number,
    params?: QualificationTransferPendingSearchParams
  ): Promise<ListQualificationTransferPendingResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferPendingResponse>(
        this.getCompletePath(`/${policyId}/pending-evaluations`),
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as avaliações de repasse pendentes.')
    }
  }

  async listCompanyDomain(policyId: number): Promise<ListQualificationTransferDomainResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferDomainResponse>(
        this.getCompletePath(`/${policyId}/domain/companies`)
      )
      return data
    } catch (e) {
      // fallback intencional: os domínios alimentam apenas os chips de abrangência
      // (leitura auxiliar). Uma falha aqui não deve derrubar o carregamento da
      // política — a tela renderiza a abrangência vazia em vez de quebrar.
      return { data: [], count: 0 }
    }
  }

  async listHealthUnitDomain(policyId: number): Promise<ListQualificationTransferDomainResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferDomainResponse>(
        this.getCompletePath(`/${policyId}/domain/health-units`)
      )
      return data
    } catch (e) {
      // fallback intencional: os domínios alimentam apenas os chips de abrangência
      // (leitura auxiliar). Uma falha aqui não deve derrubar o carregamento da
      // política — a tela renderiza a abrangência vazia em vez de quebrar.
      return { data: [], count: 0 }
    }
  }

  async listScheduleDomain(policyId: number): Promise<ListQualificationTransferDomainResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferDomainResponse>(
        this.getCompletePath(`/${policyId}/domain/schedules`)
      )
      return data
    } catch (e) {
      // fallback intencional: os domínios alimentam apenas os chips de abrangência
      // (leitura auxiliar). Uma falha aqui não deve derrubar o carregamento da
      // política — a tela renderiza a abrangência vazia em vez de quebrar.
      return { data: [], count: 0 }
    }
  }

  async listPositionDomain(policyId: number): Promise<ListQualificationTransferDomainResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferDomainResponse>(
        this.getCompletePath(`/${policyId}/domain/positions`)
      )
      return data
    } catch (e) {
      // fallback intencional: os domínios alimentam apenas os chips de abrangência
      // (leitura auxiliar). Uma falha aqui não deve derrubar o carregamento da
      // política — a tela renderiza a abrangência vazia em vez de quebrar.
      return { data: [], count: 0 }
    }
  }

  async listScheduleLinks(scheduleId: number): Promise<ListQualificationTransferLinksResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferLinksResponse>(
        `/schedules/${scheduleId}/qualification-transfer-links`
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de repasse da escala.'
      )
    }
  }

  async createScheduleLink(
    scheduleId: number,
    params: CreateQualificationTransferLinkRequest
  ): Promise<QualificationTransferLink> {
    try {
      const { data } = await request.post<QualificationTransferLink>(
        `/schedules/${scheduleId}/qualification-transfer-links`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível vincular a política de repasse à escala.')
    }
  }

  async updateScheduleLink(
    scheduleId: number,
    linkId: number,
    params: UpdateQualificationTransferLinkRequest
  ): Promise<QualificationTransferLink> {
    try {
      const { data } = await request.put<QualificationTransferLink>(
        `/schedules/${scheduleId}/qualification-transfer-links/${linkId}`,
        params
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível editar o vínculo de política de repasse da escala.'
      )
    }
  }

  async deleteScheduleLink(scheduleId: number, linkId: number): Promise<void> {
    try {
      await request.delete(`/schedules/${scheduleId}/qualification-transfer-links/${linkId}`)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível excluir o vínculo de política de repasse da escala.'
      )
    }
  }

  // Vínculos de política de repasse por recurso (ResourcePoliciesModal).
  // O handleError preserva a message pt-BR do backend (validações de vigência do
  // PATCH: sobreposição, gap, vigência aberta etc.) dentro do ServiceError, que o
  // PolicyLinksManager exibe no toast via getApiErrorMessage.

  async listCompanyPolicyLinks(
    companyId: number
  ): Promise<ListQualificationTransferPolicyLinksResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferPolicyLinksResponse>(
        this.getCompletePath(`/company/${companyId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de repasse da empresa.'
      )
    }
  }

  async listHealthUnitPolicyLinks(
    healthUnitId: number
  ): Promise<ListQualificationTransferPolicyLinksResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferPolicyLinksResponse>(
        this.getCompletePath(`/health-unit/${healthUnitId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de repasse da unidade.'
      )
    }
  }

  async listPositionPolicyLinks(
    positionId: number
  ): Promise<ListQualificationTransferPolicyLinksResponse> {
    try {
      const { data } = await request.get<ListQualificationTransferPolicyLinksResponse>(
        this.getCompletePath(`/position/${positionId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os vínculos de política de repasse do posto.'
      )
    }
  }

  async updateCompanyPolicyLinks(
    companyId: number,
    payload: UpdateQualificationTransferPolicyLinksRequest
  ): Promise<void> {
    try {
      await request.patch(this.getCompletePath(`/company/${companyId}/policy-links`), payload)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar os vínculos de política de repasse da empresa.'
      )
    }
  }

  async updateHealthUnitPolicyLinks(
    healthUnitId: number,
    payload: UpdateQualificationTransferPolicyLinksRequest
  ): Promise<void> {
    try {
      await request.patch(
        this.getCompletePath(`/health-unit/${healthUnitId}/policy-links`),
        payload
      )
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar os vínculos de política de repasse da unidade.'
      )
    }
  }

  async updatePositionPolicyLinks(
    positionId: number,
    payload: UpdateQualificationTransferPolicyLinksRequest
  ): Promise<void> {
    try {
      await request.patch(this.getCompletePath(`/position/${positionId}/policy-links`), payload)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar os vínculos de política de repasse do posto.'
      )
    }
  }

  async listPolicyOptions(
    params?: QualificationTransferPolicyOptionsSearchParams
  ): Promise<ListQualificationTransferPolicyOptionsResponse> {
    const response = await this.list({
      q: params?.q,
      companyIds: params?.companyId ? [params.companyId] : undefined,
      offset: params?.offset,
      limit: params?.limit
    })
    return {
      data: response.data.map((policy) => ({
        id: policy.id,
        name: policy.policyName,
        company: { name: policy.company }
      })),
      count: response.count
    }
  }
}
